import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Button,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Tabs,
  Tab,
  IconButton,
  Collapse,
  alpha,
  useTheme,
  CircularProgress,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import {
  MergeType as ConsolidationIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Search as SearchIcon,
  Store as BranchIcon,
  ShoppingCart as OrderIcon,
  Inventory as ItemIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  consolidationService,
  ConsolidationResultDto,
  PricingSummaryResultDto,
} from '../../services/consolidation.service';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import apiClient from '../../services/api';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';

const translations = {
  en: {
    title: 'Consolidation Report',
    titleAr: 'تقرير التوحيد',
    tabConsolidation: 'Consolidation View',
    tabPricing: 'Pricing Summary',
    dateFrom: 'From Date',
    dateTo: 'To Date',
    supplier: 'Supplier',
    allSuppliers: 'All Suppliers',
    search: 'Search items...',
    documentType: 'Document Type',
    purchaseOrders: 'Purchase Orders',
    purchaseInvoices: 'Purchase Invoices',
    refresh: 'Refresh',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    print: 'Print',
    itemCode: 'Item Code',
    itemName: 'Item',
    category: 'Category',
    unit: 'Unit',
    totalQty: 'Total Qty',
    avgPrice: 'Avg Price',
    totalAmount: 'Total Amount',
    branchName: 'Location',
    poNumber: 'Document #',
    allDocuments: 'All',
    quantity: 'Qty',
    unitPrice: 'Unit Price',
    total: 'Total',
    affectedLines: 'Lines',
    totalOrders: 'Total Orders',
    totalBranches: 'Total Branches',
    totalItems: 'Total Items',
    grandTotal: 'Grand Total',
    noData: 'No data — select a date range and click Refresh',
    loading: 'Loading...',
    kwd: 'KWD',
    selectDates: 'Please select a From Date first',
    branchDetails: 'Branch Details',
  },
  ar: {
    title: 'Consolidation Report',
    titleAr: 'تقرير التوحيد',
    tabConsolidation: 'عرض التوحيد',
    tabPricing: 'ملخص الأسعار',
    dateFrom: 'من تاريخ',
    dateTo: 'إلى تاريخ',
    supplier: 'المورد',
    allSuppliers: 'جميع الموردين',
    search: 'بحث عن صنف...',
    documentType: 'نوع المستند',
    purchaseOrders: 'أوامر الشراء',
    purchaseInvoices: 'فواتير الشراء',
    refresh: 'تحديث',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    print: 'طباعة',
    itemCode: 'كود الصنف',
    itemName: 'الصنف',
    category: 'الفئة',
    unit: 'الوحدة',
    totalQty: 'إجمالي الكمية',
    avgPrice: 'متوسط السعر',
    totalAmount: 'الإجمالي',
    branchName: 'الموقع',
    poNumber: 'رقم المستند',
    allDocuments: 'الكل',
    quantity: 'الكمية',
    unitPrice: 'سعر الوحدة',
    total: 'الإجمالي',
    affectedLines: 'الأسطر',
    totalOrders: 'عدد الأوامر',
    totalBranches: 'عدد الفروع',
    totalItems: 'عدد الأصناف',
    grandTotal: 'الإجمالي الكلي',
    noData: 'لا توجد بيانات — اختر نطاق تاريخ ثم اضغط تحديث',
    loading: 'جاري التحميل...',
    kwd: 'د.ك',
    selectDates: 'الرجاء اختيار تاريخ البداية أولاً',
    branchDetails: 'تفاصيل الفروع',
  }
};

interface Supplier {
  supplierId: number;
  supplierCode: string;
  supplierName: string;
  supplierNameAr: string;
}

const ConsolidationReport: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { minDate, maxDate } = useFiscalDateRange();

  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  const [consolidationData, setConsolidationData] = useState<ConsolidationResultDto | null>(null);
  const [pricingData, setPricingData] = useState<PricingSummaryResultDto | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState<number | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [documentType, setDocumentType] = useState<'PurchaseOrders' | 'PurchaseInvoices'>('PurchaseOrders');
  const [consolidationDocType, setConsolidationDocType] = useState<'All' | 'PurchaseOrders' | 'PurchaseInvoices'>('All');

  useEffect(() => {
    companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(console.error);
    apiClient.get<Supplier[]>('/Suppliers').then(r => setSuppliers(r.data)).catch(console.error);
  }, []);

  const loadData = async () => {
    if (!dateFromFilter) {
      toast.warning(t.selectDates);
      return;
    }
    setLoading(true);
    setExpandedRows(new Set());
    try {
      if (currentTab === 0) {
        const result = await consolidationService.getConsolidation({
          fromDate: dateFromFilter,
          toDate: dateToFilter || undefined,
          documentType: consolidationDocType,
          supplierId: supplierFilter || undefined,
          search: searchQuery || undefined,
        });
        setConsolidationData(result);
      } else {
        const result = await consolidationService.getPricingSummary({
          fromDate: dateFromFilter,
          toDate: dateToFilter || undefined,
          documentType,
          supplierId: supplierFilter || undefined,
          search: searchQuery || undefined,
        });
        setPricingData(result);
      }
    } catch {
      toast.error(isRTL ? 'فشل تحميل البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (id: number) => {
    setExpandedRows(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const formatNumber = (n: number, d = 3) =>
    n.toLocaleString('en-GB', { minimumFractionDigits: d, maximumFractionDigits: d });

  const getFilteredItems = () => {
    const items = currentTab === 0
      ? (consolidationData?.items || [])
      : (pricingData?.items || []);
    if (!searchQuery) return items;
    const q = searchQuery.toLowerCase();
    return items.filter(i =>
      i.itemCode.toLowerCase().includes(q) ||
      i.itemName.toLowerCase().includes(q) ||
      i.itemNameAr.toLowerCase().includes(q)
    );
  };

  // ── Export Excel ─────────────────────────────────────────────────────────────
  const exportToExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(t.title);

    if (companyInfo?.logoBase64) {
      try {
        const img = wb.addImage({ base64: companyInfo.logoBase64, extension: 'png' });
        ws.addImage(img, { tl: { col: 0, row: 0 }, ext: { width: 100, height: 80 } });
      } catch { /* skip */ }
    }

    ws.mergeCells('C1:H1');
    const c1 = ws.getCell('C1');
    c1.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
    c1.font = { size: 18, bold: true, color: { argb: 'FF1565C0' } };
    c1.alignment = { horizontal: 'center', vertical: 'middle' };

    ws.mergeCells('C2:H2');
    ws.getCell('C2').value = companyInfo?.address || '';
    ws.getCell('C2').alignment = { horizontal: 'center' };

    ws.mergeCells('A3:J3');
    ws.getCell('A3').value = `KWD | ${companyInfo?.phone || ''} | ${companyInfo?.email || ''}`;
    ws.getCell('A3').font = { size: 9 };
    ws.getCell('A3').alignment = { horizontal: 'center' };

    ws.getRow(4).height = 8;

    ws.mergeCells('A5:J5');
    const titleCell = ws.getCell('A5');
    titleCell.value = `${isRTL ? t.titleAr : t.title} — ${currentTab === 0 ? t.tabConsolidation : t.tabPricing}`;
    titleCell.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
    ws.getRow(5).height = 24;

    ws.getRow(6).height = 8;

    if (currentTab === 0 && consolidationData) {
      const headers = [t.itemCode, t.itemName, t.category, t.unit, t.totalQty, t.avgPrice, t.totalAmount];
      const hRow = ws.addRow(headers);
      hRow.eachCell(cell => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
        cell.alignment = { horizontal: 'center' };
      });

      getFilteredItems().forEach(item => {
        const i = item as typeof consolidationData.items[0];
        ws.addRow([
          i.itemCode,
          isRTL ? i.itemNameAr : i.itemName,
          isRTL ? i.categoryNameAr : i.categoryName,
          isRTL ? i.unitNameAr : i.unitName,
          i.totalQuantity,
          i.averageUnitPrice,
          i.totalAmount,
        ]);
        // Branch sub-rows
        i.branchDetails.forEach(b => {
          const bRow = ws.addRow(['', `  → ${isRTL ? b.branchNameAr : b.branchName}`, b.documentNumber || b.poNumber, '', b.quantity, b.unitPrice, b.totalPrice]);
          bRow.eachCell(cell => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBEAFE' } }; });
        });
      });

      ws.addRow([]);
      const totalRow = ws.addRow([t.grandTotal, '', '', '', '', '', consolidationData.grandTotal]);
      totalRow.eachCell(cell => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0D47A1' } };
      });
    } else if (currentTab === 1 && pricingData) {
      const headers = [t.itemCode, t.itemName, t.category, t.unit, t.totalQty, t.unitPrice, t.affectedLines];
      const hRow = ws.addRow(headers);
      hRow.eachCell(cell => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
        cell.alignment = { horizontal: 'center' };
      });
      getFilteredItems().forEach(item => {
        const i = item as typeof pricingData.items[0];
        ws.addRow([
          i.itemCode,
          isRTL ? i.itemNameAr : i.itemName,
          isRTL ? i.categoryNameAr : i.categoryName,
          isRTL ? i.unitNameAr : i.unitName,
          i.totalQuantity,
          i.currentUnitPrice,
          i.affectedLines,
        ]);
      });
    }

    ws.columns.forEach(col => { col.width = 18; });
    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `consolidation-${new Date().toISOString().split('T')[0]}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Export PDF ─────────────────────────────────────────────────────────────
  const exportToPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(14);
    doc.text(`${isRTL ? t.titleAr : t.title} — ${currentTab === 0 ? t.tabConsolidation : t.tabPricing}`,
      doc.internal.pageSize.width / 2, 15, { align: 'center' });
    doc.setFontSize(9);
    doc.text(new Date().toLocaleDateString('en-GB'), doc.internal.pageSize.width / 2, 22, { align: 'center' });

    const items = getFilteredItems();
    if (currentTab === 0) {
      autoTable(doc, {
        startY: 28,
        head: [[t.itemCode, t.itemName, t.category, t.unit, t.totalQty, t.avgPrice, t.totalAmount]],
        body: items.map(i => {
          const ci = i as ConsolidationResultDto['items'][0];
          return [ci.itemCode, isRTL ? ci.itemNameAr : ci.itemName, isRTL ? ci.categoryNameAr : ci.categoryName,
            isRTL ? ci.unitNameAr : ci.unitName, formatNumber(ci.totalQuantity, 2),
            formatNumber(ci.averageUnitPrice), formatNumber(ci.totalAmount)];
        }),
        headStyles: { fillColor: [21, 101, 192], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [240, 248, 255] },
        styles: { fontSize: 8 },
      });
    } else {
      autoTable(doc, {
        startY: 28,
        head: [[t.itemCode, t.itemName, t.category, t.unit, t.totalQty, t.unitPrice, t.affectedLines]],
        body: items.map(i => {
          const pi = i as PricingSummaryResultDto['items'][0];
          return [pi.itemCode, isRTL ? pi.itemNameAr : pi.itemName, isRTL ? pi.categoryNameAr : pi.categoryName,
            isRTL ? pi.unitNameAr : pi.unitName, formatNumber(pi.totalQuantity, 2),
            formatNumber(pi.currentUnitPrice), pi.affectedLines];
        }),
        headStyles: { fillColor: [21, 101, 192], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [240, 248, 255] },
        styles: { fontSize: 8 },
      });
    }
    doc.save(`consolidation-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const handlePrint = () => window.print();

  const stats = consolidationData
    ? [
        { label: t.totalOrders, value: consolidationData.totalOrders, icon: <OrderIcon />, color: 'primary.main' },
        { label: t.totalBranches, value: consolidationData.totalBranches, icon: <BranchIcon />, color: 'info.main' },
        { label: t.totalItems, value: consolidationData.totalItems, icon: <ItemIcon />, color: 'warning.main' },
        { label: `${t.grandTotal} (${t.kwd})`, value: formatNumber(consolidationData.grandTotal), icon: <MoneyIcon />, color: 'success.main' },
      ]
    : pricingData
    ? [
        { label: t.totalOrders, value: pricingData.totalDocuments, icon: <OrderIcon />, color: 'primary.main' },
        { label: t.totalItems, value: pricingData.items.length, icon: <ItemIcon />, color: 'warning.main' },
        { label: 'Lines', value: pricingData.totalLines, icon: <MoneyIcon />, color: 'success.main' },
        { label: 'Document Type', value: pricingData.documentType, icon: <BranchIcon />, color: 'info.main' },
      ]
    : [];

  return (
    <Box sx={{ p: 3, direction: isRTL ? 'rtl' : 'ltr' }}>

      {/* Print Header */}
      <Box className="print-only" sx={{ mb: 3, textAlign: 'center' }}>
        {companyInfo?.logoBase64 && <img src={companyInfo.logoBase64} alt="Logo" style={{ height: 60 }} />}
        <Typography variant="h4" fontWeight="bold">{isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}</Typography>
        <Typography variant="h5" sx={{ mt: 1 }}>{isRTL ? t.titleAr : t.title}</Typography>
        <Typography variant="body2">{new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</Typography>
      </Box>

      {/* Screen Header */}
      <Box className="no-print" sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), p: 1.5, borderRadius: 2 }}>
          <ConsolidationIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight="bold" color="primary">{isRTL ? t.titleAr : t.title}</Typography>
          <Typography variant="caption" color="text.secondary">
            {isRTL ? 'توحيد أوامر الشراء من جميع الفروع' : 'Consolidated purchase orders across all branches'}
          </Typography>
        </Box>
      </Box>
      <Divider className="no-print" sx={{ mb: 2 }} />

      {/* Tabs */}
      <Paper className="no-print" sx={{ mb: 2 }}>
        <Tabs value={currentTab} onChange={(_, v) => { setCurrentTab(v); setConsolidationData(null); setPricingData(null); }}>
          <Tab label={t.tabConsolidation} />
          <Tab label={t.tabPricing} />
        </Tabs>
      </Paper>

      {/* Stats Cards */}
      {stats.length > 0 && (
        <Box className="no-print" sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {stats.map((card, i) => (
            <Card key={i} sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 180 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">{card.value}</Typography>
                    <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                  </Box>
                  {React.cloneElement(card.icon, { sx: { fontSize: 50, opacity: 0.3, color: card.color } })}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Filters */}
      <Paper className="no-print" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker label={t.dateFrom} value={dateFromFilter ? new Date(dateFromFilter) : null}
              onChange={(d) => {
                const newValue = d ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` : '';
                setDateFromFilter(newValue);
                if (dateToFilter && newValue && dateToFilter < newValue) setDateToFilter('');
              }}
              format="dd/MM/yyyy" views={['year','month','day']} openTo="day"
              minDate={minDate} maxDate={dateToFilter ? new Date(dateToFilter) : maxDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }} />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker label={t.dateTo} value={dateToFilter ? new Date(dateToFilter) : null}
              onChange={(d) => {
                const newValue = d ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` : '';
                setDateToFilter(newValue);
                if (dateFromFilter && newValue && dateFromFilter > newValue) setDateFromFilter('');
              }}
              format="dd/MM/yyyy" views={['year','month','day']} openTo="day"
              minDate={dateFromFilter ? new Date(dateFromFilter) : minDate} maxDate={maxDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }} />
          </LocalizationProvider>
          <Autocomplete
            options={suppliers}
            getOptionLabel={(o) => isRTL ? o.supplierNameAr : o.supplierName}
            value={suppliers.find(s => s.supplierId === supplierFilter) || null}
            onChange={(_, v) => setSupplierFilter(v?.supplierId || '')}
            renderInput={(params) => <TextField {...params} label={t.supplier} size="small" />}
            sx={{ minWidth: 220 }}
          />
          {currentTab === 0 && (
            <TextField
              select size="small" label={t.documentType} value={consolidationDocType}
              onChange={(e) => setConsolidationDocType(e.target.value as any)} sx={{ minWidth: 180 }}>
              <MenuItem value="All">{t.allDocuments}</MenuItem>
              <MenuItem value="PurchaseOrders">{t.purchaseOrders}</MenuItem>
              <MenuItem value="PurchaseInvoices">{t.purchaseInvoices}</MenuItem>
            </TextField>
          )}
          {currentTab === 1 && (
            <TextField
              select size="small" label={t.documentType} value={documentType}
              onChange={(e) => setDocumentType(e.target.value as any)} sx={{ minWidth: 180 }}>
              <MenuItem value="PurchaseOrders">{t.purchaseOrders}</MenuItem>
              <MenuItem value="PurchaseInvoices">{t.purchaseInvoices}</MenuItem>
            </TextField>
          )}
          <TextField
            size="small"
            placeholder={t.search}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 220 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button startIcon={<RefreshIcon />} onClick={loadData} variant="contained">{t.refresh}</Button>
          <Button startIcon={<ExcelIcon />} onClick={exportToExcel} variant="contained" color="success" disabled={!consolidationData && !pricingData}>{t.exportExcel}</Button>
          <Button startIcon={<PdfIcon />} onClick={exportToPdf} variant="contained" color="error" disabled={!consolidationData && !pricingData}>{t.exportPdf}</Button>
          <Button startIcon={<PrintIcon />} onClick={handlePrint} variant="contained">{t.print}</Button>
        </Box>
      </Paper>

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
      ) : currentTab === 0 ? (

        /* ── Consolidation Tab ────────────────────────────────────────────────── */
        <Paper>
          <TableContainer sx={{ maxHeight: 620 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40, bgcolor: '#1565C0' }} />
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.itemCode}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.itemName}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.category}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.unit}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.totalQty}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.avgPrice} ({t.kwd})</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.totalAmount} ({t.kwd})</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {consolidationData
                  ? getFilteredItems().map((item, idx) => {
                      const ci = item as ConsolidationResultDto['items'][0];
                      return (
                        <React.Fragment key={ci.itemId}>
                          <TableRow
                            sx={{
                              cursor: 'pointer',
                              bgcolor: expandedRows.has(ci.itemId)
                                ? alpha(theme.palette.primary.main, 0.08)
                                : idx % 2 === 0 ? 'background.default' : 'action.hover',
                              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                            }}
                            onClick={() => toggleRow(ci.itemId)}
                          >
                            <TableCell>
                              <IconButton size="small">
                                {expandedRows.has(ci.itemId) ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '12px' }}>{ci.itemCode}</TableCell>
                            <TableCell sx={{ fontWeight: 500 }}>{isRTL ? ci.itemNameAr : ci.itemName}</TableCell>
                            <TableCell>{isRTL ? ci.categoryNameAr : ci.categoryName}</TableCell>
                            <TableCell>
                              <Chip label={isRTL ? ci.unitNameAr : ci.unitName} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(ci.totalQuantity, 2)}</TableCell>
                            <TableCell align="right">{formatNumber(ci.averageUnitPrice)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{formatNumber(ci.totalAmount)}</TableCell>
                          </TableRow>

                          {/* Branch Details */}
                          <TableRow>
                            <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                              <Collapse in={expandedRows.has(ci.itemId)} unmountOnExit>
                                <Box sx={{ p: 1.5, bgcolor: isDark ? alpha('#1565C0', 0.06) : '#E3F2FD' }}>
                                  <Typography variant="caption" fontWeight="bold" color="primary" sx={{ mb: 0.5, display: 'block' }}>
                                    {t.branchDetails} — {isRTL ? ci.itemNameAr : ci.itemName}
                                  </Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow sx={{ bgcolor: '#1976D2' }}>
                                        <TableCell sx={{ color: 'white', fontSize: '11px' }}>{t.branchName}</TableCell>
                                        <TableCell sx={{ color: 'white', fontSize: '11px' }}>{t.poNumber}</TableCell>
                                        <TableCell align="right" sx={{ color: 'white', fontSize: '11px' }}>{t.quantity}</TableCell>
                                        <TableCell align="right" sx={{ color: 'white', fontSize: '11px' }}>{t.unitPrice}</TableCell>
                                        <TableCell align="right" sx={{ color: 'white', fontSize: '11px' }}>{t.total}</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {ci.branchDetails.map((b, bi) => (
                                        <TableRow key={bi} sx={{ bgcolor: bi % 2 === 0 ? 'background.paper' : alpha('#1565C0', isDark ? 0.15 : 0.04) }}>
                                          <TableCell sx={{ fontSize: '11px', fontWeight: 500 }}>{isRTL ? b.branchNameAr : b.branchName}</TableCell>
                                          <TableCell sx={{ fontSize: '11px', fontFamily: 'monospace' }}>{b.documentNumber || b.poNumber}</TableCell>
                                          <TableCell align="right" sx={{ fontSize: '11px' }}>{formatNumber(b.quantity, 2)}</TableCell>
                                          <TableCell align="right" sx={{ fontSize: '11px' }}>{formatNumber(b.unitPrice)}</TableCell>
                                          <TableCell align="right" sx={{ fontSize: '11px', fontWeight: 'bold' }}>{formatNumber(b.totalPrice)}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                        <ConsolidationIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 1 }} />
                        <Typography color="text.secondary">{t.noData}</Typography>
                      </TableCell>
                    </TableRow>
                  )
                }

                {/* Grand Total */}
                {consolidationData && (
                  <TableRow sx={{ bgcolor: '#0D47A1' }}>
                    <TableCell />
                    <TableCell colSpan={4} sx={{ color: 'white', fontWeight: 'bold' }}>{t.grandTotal}</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                      {formatNumber(consolidationData.items.reduce((s, i) => s + i.totalQuantity, 0), 2)}
                    </TableCell>
                    <TableCell />
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', fontSize: '13px' }}>
                      {formatNumber(consolidationData.grandTotal)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

      ) : (

        /* ── Pricing Summary Tab ──────────────────────────────────────────────── */
        <Paper>
          <TableContainer sx={{ maxHeight: 620 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40, bgcolor: '#1565C0' }} />
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.itemCode}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.itemName}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.category}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.unit}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.totalQty}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.unitPrice} ({t.kwd})</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#1565C0', color: 'white' }}>{t.affectedLines}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pricingData
                  ? getFilteredItems().map((item, idx) => {
                      const pi = item as PricingSummaryResultDto['items'][0];
                      return (
                        <React.Fragment key={pi.itemId}>
                          <TableRow
                            sx={{
                              cursor: 'pointer',
                              bgcolor: expandedRows.has(pi.itemId)
                                ? alpha(theme.palette.primary.main, 0.08)
                                : idx % 2 === 0 ? 'background.default' : 'action.hover',
                              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                            }}
                            onClick={() => toggleRow(pi.itemId)}
                          >
                            <TableCell>
                              <IconButton size="small">
                                {expandedRows.has(pi.itemId) ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '12px' }}>{pi.itemCode}</TableCell>
                            <TableCell sx={{ fontWeight: 500 }}>{isRTL ? pi.itemNameAr : pi.itemName}</TableCell>
                            <TableCell>{isRTL ? pi.categoryNameAr : pi.categoryName}</TableCell>
                            <TableCell>
                              <Chip label={isRTL ? pi.unitNameAr : pi.unitName} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell align="right">{formatNumber(pi.totalQuantity, 2)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{formatNumber(pi.currentUnitPrice)}</TableCell>
                            <TableCell align="right">
                              <Chip label={pi.affectedLines} size="small" color="info" />
                            </TableCell>
                          </TableRow>

                          {/* Per-Branch Prices */}
                          <TableRow>
                            <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                              <Collapse in={expandedRows.has(pi.itemId)} unmountOnExit>
                                <Box sx={{ p: 1.5, bgcolor: isDark ? alpha('#1565C0', 0.06) : '#E3F2FD' }}>
                                  <Typography variant="caption" fontWeight="bold" color="primary" sx={{ mb: 0.5, display: 'block' }}>
                                    {t.branchDetails} — {isRTL ? pi.itemNameAr : pi.itemName}
                                  </Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow sx={{ bgcolor: '#1976D2' }}>
                                        <TableCell sx={{ color: 'white', fontSize: '11px' }}>{t.branchName}</TableCell>
                                        <TableCell align="right" sx={{ color: 'white', fontSize: '11px' }}>{t.unitPrice}</TableCell>
                                        <TableCell align="right" sx={{ color: 'white', fontSize: '11px' }}>{t.quantity}</TableCell>
                                        <TableCell align="right" sx={{ color: 'white', fontSize: '11px' }}>{t.affectedLines}</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {pi.branchPrices.map((b, bi) => (
                                        <TableRow key={bi} sx={{ bgcolor: bi % 2 === 0 ? 'background.paper' : alpha('#1565C0', isDark ? 0.15 : 0.04) }}>
                                          <TableCell sx={{ fontSize: '11px', fontWeight: 500 }}>{isRTL ? b.branchNameAr : b.branchName}</TableCell>
                                          <TableCell align="right" sx={{ fontSize: '11px', fontWeight: 'bold' }}>{formatNumber(b.currentUnitPrice)}</TableCell>
                                          <TableCell align="right" sx={{ fontSize: '11px' }}>{formatNumber(b.quantity, 2)}</TableCell>
                                          <TableCell align="right" sx={{ fontSize: '11px' }}>{b.affectedLines}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                        <ConsolidationIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 1 }} />
                        <Typography color="text.secondary">{t.noData}</Typography>
                      </TableCell>
                    </TableRow>
                  )
                }
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          @page { margin: 1cm; size: landscape; }
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
        }
        .print-only { display: none; }
      `}</style>
    </Box>
  );
};

export default ConsolidationReport;
